/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  AlertLifecycleStage,
  AlertTarget,
  AlertUrgency,
  EntityType,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface AlertProps {
  id: string;
  entityType: EntityType;
  activationRuleId: string;
  urgency: AlertUrgency;
  title: string | null;
  content: string;
  effectiveDate: Date | null;
  expirationDate: Date | null;
  paused: boolean | null;
  lifecycleStage: AlertLifecycleStage;
  requiresAcknowledgement: boolean;
  createdById: string | null;
  isAudible: boolean;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class AlertRecord extends Record<AlertProps>({
  id: null as any,
  entityType: null as any,
  activationRuleId: null as any,
  urgency: null as any,
  title: null as any,
  content: null as any,
  effectiveDate: null as any,
  expirationDate: null as any,
  paused: null as any,
  lifecycleStage: null as any,
  requiresAcknowledgement: null as any,
  createdById: null as any,
  isAudible: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    entityType: EntityType;
    activationRuleId: string;
    urgency: AlertUrgency;
    title: string | null;
    content: string;
    effectiveDate: string | number | Date | null;
    expirationDate: string | number | Date | null;
    paused: boolean | null;
    lifecycleStage: AlertLifecycleStage;
    requiresAcknowledgement: boolean;
    createdById: string | null;
    isAudible: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): AlertRecord {
    if (json == null) {
      throw new Error('AlertRecord.fromJS: parameter cannot be null');
    }
    return new AlertRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'AlertJson.id',
      ),
      entityType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
        'AlertJson.entityType',
      ),
      activationRuleId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'activationRuleId', '00000000-0000-0000-0000-000000000000'),
        'AlertJson.activationRuleId',
      ),
      urgency: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'urgency', AlertUrgency.Information),
        'AlertJson.urgency',
      ),
      title: json.title,
      content: getFromOrThrow(json, (j) => j.content, 'AlertJson.content'),
      effectiveDate: json.effectiveDate != null ? new Date(json.effectiveDate) : null,
      expirationDate: json.expirationDate != null ? new Date(json.expirationDate) : null,
      paused: json.paused,
      lifecycleStage: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'lifecycleStage', AlertLifecycleStage.Unspecified),
        'AlertJson.lifecycleStage',
      ),
      requiresAcknowledgement: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requiresAcknowledgement', false),
        'AlertJson.requiresAcknowledgement',
      ),
      createdById: json.createdById,
      isAudible: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isAudible', false),
        'AlertJson.isAudible',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    entityType: EntityType;
    activationRuleId: string;
    urgency: AlertUrgency;
    title: string | null;
    content: string;
    effectiveDate: string | number | Date | null;
    expirationDate: string | number | Date | null;
    paused: boolean | null;
    lifecycleStage: AlertLifecycleStage;
    requiresAcknowledgement: boolean;
    createdById: string | null;
    isAudible: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [AlertRecord, Error[]] {
    if (json == null) {
      throw new Error('AlertRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new AlertRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'AlertJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
            'AlertJson.entityType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activationRuleId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'activationRuleId', '00000000-0000-0000-0000-000000000000'),
            'AlertJson.activationRuleId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      urgency: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'urgency', AlertUrgency.Information),
            'AlertJson.urgency',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      title: tryOrDefault(
        () => json.title,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      content: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.content, 'AlertJson.content'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      effectiveDate: tryOrDefault(
        () => (json.effectiveDate != null ? new Date(json.effectiveDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      expirationDate: tryOrDefault(
        () => (json.expirationDate != null ? new Date(json.expirationDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      paused: tryOrDefault(
        () => json.paused,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lifecycleStage: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'lifecycleStage', AlertLifecycleStage.Unspecified),
            'AlertJson.lifecycleStage',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requiresAcknowledgement: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'requiresAcknowledgement', false),
            'AlertJson.requiresAcknowledgement',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      createdById: tryOrDefault(
        () => json.createdById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isAudible: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isAudible', false),
            'AlertJson.isAudible',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: AlertProps) {
    super(props);
    if (props == null) {
      throw new Error('AlertRecord constructor parameter cannot be null');
    }
  }
}

export abstract class AlertViewModelBase implements AlertProps {
  protected readonly _props: AlertProps;
  private _json?: AlertProps;

  get id(): string {
    return this._props.id;
  }
  get entityType(): EntityType {
    return this._props.entityType;
  }
  get activationRuleId(): string {
    return this._props.activationRuleId;
  }
  get urgency(): AlertUrgency {
    return this._props.urgency;
  }
  get title(): string | null {
    return this._props.title;
  }
  get content(): string {
    return this._props.content;
  }
  get effectiveDate(): Date | null {
    return this._props.effectiveDate;
  }
  get expirationDate(): Date | null {
    return this._props.expirationDate;
  }
  get paused(): boolean | null {
    return this._props.paused;
  }
  get lifecycleStage(): AlertLifecycleStage {
    return this._props.lifecycleStage;
  }
  get requiresAcknowledgement(): boolean {
    return this._props.requiresAcknowledgement;
  }
  get createdById(): string | null {
    return this._props.createdById;
  }
  get isAudible(): boolean {
    return this._props.isAudible;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAirport() {
    return this._props.entityType === EntityType.Airport;
  }
  public get isBase() {
    return this._props.entityType === EntityType.Base;
  }
  public get isCard() {
    return this._props.entityType === EntityType.Card;
  }
  public get isCommunicationCenter() {
    return this._props.entityType === EntityType.CommunicationCenter;
  }
  public get isCompany() {
    return this._props.entityType === EntityType.Company;
  }
  public get isControlZone() {
    return this._props.entityType === EntityType.ControlZone;
  }
  public get isLocation() {
    return this._props.entityType === EntityType.Location;
  }
  public get isMedicalFacility() {
    return this._props.entityType === EntityType.MedicalFacility;
  }
  public get isOutOfServiceEvent() {
    return this._props.entityType === EntityType.OutOfServiceEvent;
  }
  public get isPaipEvent() {
    return this._props.entityType === EntityType.PaipEvent;
  }
  public get isPerson() {
    return this._props.entityType === EntityType.Person;
  }
  public get isProgram() {
    return this._props.entityType === EntityType.Program;
  }
  public get isRegion() {
    return this._props.entityType === EntityType.Region;
  }
  public get isTimer() {
    return this._props.entityType === EntityType.Timer;
  }
  public get isUnit() {
    return this._props.entityType === EntityType.Unit;
  }
  public get isEntityTypeUnspecified() {
    return this._props.entityType === EntityType.Unspecified;
  }
  public get isLifecycleStageUnspecified() {
    return this._props.lifecycleStage === AlertLifecycleStage.Unspecified;
  }
  public get isVehicle() {
    return this._props.entityType === EntityType.Vehicle;
  }
  public get isCritical() {
    return this._props.urgency === AlertUrgency.Critical;
  }
  public get isInformation() {
    return this._props.urgency === AlertUrgency.Information;
  }
  public get isWarning() {
    return this._props.urgency === AlertUrgency.Warning;
  }
  public get isPreAssignment() {
    return this._props.lifecycleStage === AlertLifecycleStage.PreAssignment;
  }

  public isAnyEntityType(...entityType: EntityType[]) {
    return entityType.includes(this._props.entityType);
  }
  public isAnyUrgency(...alertUrgency: AlertUrgency[]) {
    return alertUrgency.includes(this._props.urgency);
  }
  public isAnyLifecycleStage(...alertLifecycleStage: AlertLifecycleStage[]) {
    return alertLifecycleStage.includes(this._props.lifecycleStage);
  }

  constructor(props: AlertProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        entityType: this._props.entityType,
        activationRuleId: this._props.activationRuleId,
        urgency: this._props.urgency,
        title: this._props.title,
        content: this._props.content,
        effectiveDate: this._props.effectiveDate,
        expirationDate: this._props.expirationDate,
        paused: this._props.paused,
        lifecycleStage: this._props.lifecycleStage,
        requiresAcknowledgement: this._props.requiresAcknowledgement,
        createdById: this._props.createdById,
        isAudible: this._props.isAudible,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllAlertEntities = (state: AppState) => state.entityStores.alert.entities;

export const selectAlertById = (id: string) => (state: AppState) =>
  state.entityStores.alert.entities.get(id);

export const selectAlertMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.alert.meta.get(id);

export const selectAlertAndMetadataById = (id: string) =>
  createSelector(selectAlertById(id), selectAlertMetadataById(id), (alert, meta) => ({
    alert,
    meta,
  }));

export function selectAlertEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllAlertEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, AlertRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectAlertEntitiesByEntityType(entityType: EntityType) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.entityType === entityType).valueSeq(),
  );
}

export function selectAlertEntitiesByActivationRuleId(activationRuleId: string) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.activationRuleId === activationRuleId).valueSeq(),
  );
}

export function selectAlertEntitiesByUrgency(urgency: AlertUrgency) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.urgency === urgency).valueSeq(),
  );
}

export function selectAlertEntitiesByTitle(title: string) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.title === title).valueSeq(),
  );
}

export function selectAlertEntitiesByContent(content: string) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.content === content).valueSeq(),
  );
}

export function selectAlertEntitiesByEffectiveDate(effectiveDate: Date) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.effectiveDate === effectiveDate).valueSeq(),
  );
}

export function selectAlertEntitiesByExpirationDate(expirationDate: Date) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.expirationDate === expirationDate).valueSeq(),
  );
}

export function selectAlertEntitiesByPaused(paused: boolean) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.paused === paused).valueSeq(),
  );
}

export function selectAlertEntitiesByLifecycleStage(lifecycleStage: AlertLifecycleStage) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.lifecycleStage === lifecycleStage).valueSeq(),
  );
}

export function selectAlertEntitiesByRequiresAcknowledgement(requiresAcknowledgement: boolean) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.requiresAcknowledgement === requiresAcknowledgement).valueSeq(),
  );
}

export function selectAlertEntitiesByCreatedById(createdById: string) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.createdById === createdById).valueSeq(),
  );
}

export function selectAlertEntitiesByIsAudible(isAudible: boolean) {
  return createSelector(selectAllAlertEntities, (entities) =>
    entities.filter((e) => e.isAudible === isAudible).valueSeq(),
  );
}
//#endregion Selectors
